import React, { useEffect, useState } from 'react'
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message
} from 'antd'
import dayjs from 'dayjs'

import api, { getApiErrorMessage } from '../../utils/api'
import { addInstallmentPayment } from '../../api/installment'
import { fromPicker, todayKey } from '../../utils/timezone'
import ResponsiveSelect from '../common/ResponsiveSelect'
import Hint from '../common/Hint'
import {
  INSTALLMENT_METHOD_LABEL,
  defaultPayAmount,
  installmentMoney as money,
  pendingPeriods
} from './installmentShared'

/**
 * 登錄一筆分期收款。
 *
 * 分期收款頁、顧客資料頁、結帳頁共用同一個視窗 ——
 * 換員工、換頁面收款，看到的欄位與預設值都一樣。
 *
 * 收款日期一定要填「實際收到錢那天」：現金流是以 InstallmentPayment.paidAt 計入的。
 *
 * props:
 *   open, plan, businessId
 *   onClose()
 *   onPaid(updatedPlan)  收款成功後呼叫（updatedPlan 可能為 null）
 */
const InstallmentPaymentModal = ({ open, plan, businessId, onClose, onPaid }) => {
  const [form] = Form.useForm()
  const [paying, setPaying] = useState(false)
  const [balance, setBalance] = useState(null)
  const method = Form.useWatch('method', form)

  useEffect(() => {
    if (!open || !plan) return
    const dueNowPeriods = plan.dueNowPeriods || []
    const firstPending = pendingPeriods(plan)[0]
    form.setFieldsValue({
      amount: defaultPayAmount(plan),
      // DatePicker 吃瀏覽器時區的牆上日期；用店家時區的「今天」字串建立，
      // 送出時再用 fromPicker 換回店家時區
      paidDate: dayjs(todayKey()),
      method: 'cash',
      // 一次要收好幾期（逾期累積）時不指定期別，交給後端從最早的依序沖銷
      period: dueNowPeriods.length > 1 ? undefined : dueNowPeriods[0] ?? firstPending?.period,
      remarks: ''
    })
    setBalance(null)
  }, [open, plan, form])

  // 選儲值金扣款時順便把餘額撈出來，櫃檯不必再切去顧客資料頁看
  useEffect(() => {
    if (!open || method !== 'storedValue' || !plan?.customerId || !businessId) {
      return
    }
    let cancelled = false
    api
      .get(`/api/businesses/${businessId}/customers/${plan.customerId}/balance`)
      .then((res) => {
        if (cancelled) return
        const data = res?.data?.data ?? res?.data
        setBalance(Number(data?.balance) || 0)
      })
      .catch(() => {
        if (!cancelled) setBalance(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, method, plan?.customerId, businessId])

  const handleOk = async () => {
    const values = await form.validateFields()
    setPaying(true)
    try {
      const res = await addInstallmentPayment(businessId, plan._id, {
        amount: values.amount,
        paidDate: fromPicker(values.paidDate).format('YYYY-MM-DD'),
        method: values.method,
        period: values.period || null,
        remarks: values.remarks || ''
      })
      message.success('已登錄收款')
      onPaid && onPaid(res?.data?.data?.plan || null)
    } catch (err) {
      message.error(getApiErrorMessage(err, '登錄收款失敗'))
    } finally {
      setPaying(false)
    }
  }

  const methodOptions = Object.entries(INSTALLMENT_METHOD_LABEL)
    .filter(([value]) => value !== 'storedValue' || !!plan?.customerId)
    .map(([value, label]) => ({ value, label }))

  return (
    <Modal
      title={`登錄收款 — ${plan?.customerName || ''}`}
      open={open && !!plan}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={paying}
      okText='確認收款'
      cancelText='取消'
      destroyOnClose
    >
      <div
        style={{
          background: '#fafafa',
          border: '1px solid #eee',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 13,
          marginBottom: 16,
          lineHeight: 1.8
        }}
      >
        {plan?.orderCode && <>訂單 {plan.orderCode} · </>}
        未收餘額 <strong>{money(plan?.outstanding)}</strong>
        {plan?.dueNowAmount > 0 && (
          <>
            {' '}· 本次應繳 <strong style={{ color: '#c0392b' }}>{money(plan.dueNowAmount)}</strong>
          </>
        )}
        {plan?.nextDueDate && (
          <>
            {' '}· 下期到期日 <strong>{plan.nextDueDate}</strong>
          </>
        )}
      </div>
      <Form form={form} layout='vertical'>
        <Form.Item
          name='amount'
          label='收款金額'
          rules={[{ required: true, message: '請輸入收款金額' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={plan?.outstanding || undefined}
            prefix='NT$'
          />
        </Form.Item>
        <Form.Item
          name='paidDate'
          label='收款日期'
          tooltip='填「實際收到錢那天」。現金流報表是以這個日期計入的，不是結帳日。'
          rules={[{ required: true, message: '請選擇收款日期' }]}
        >
          <DatePicker style={{ width: '100%' }} allowClear={false} inputReadOnly />
        </Form.Item>
        <Form.Item name='method' label='付款方式' rules={[{ required: true }]}>
          <ResponsiveSelect options={methodOptions} />
        </Form.Item>
        {method === 'storedValue' && (
          <div style={{ marginTop: -8, marginBottom: 16 }}>
            <Hint tone='info'>
              會直接從顧客的儲值餘額扣款
              {balance !== null && (
                <>（目前餘額 {money(balance)}）</>
              )}
              ；作廢這筆收款時會自動退回。
            </Hint>
          </div>
        )}
        <Form.Item
          name='period'
          label='沖銷期別'
          tooltip='留空的話會從最早未繳的那一期依序沖銷。'
        >
          <ResponsiveSelect
            allowClear
            placeholder='自動沖銷最早未繳的期別'
            options={pendingPeriods(plan).map((s) => ({
              value: s.period,
              label: `第 ${s.period} 期 · ${s.dueDate} · ${money(
                (s.amount || 0) - (s.paidAmount || 0)
              )}`
            }))}
          />
        </Form.Item>
        <Form.Item name='remarks' label='備註'>
          <Input.TextArea rows={2} maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default InstallmentPaymentModal
